import type { SpacingProps } from '../core/spacing';
import type { ColorVariant, TypographyVariant } from 'src/theme';
import type { FlexingProps } from 'src/components/themed/core/flexing';

import React, { memo } from 'react';
import styled, { useTheme } from 'styled-components';

import { formatFlexing } from 'src/components/themed/core/flexing';

import { formatSpacing } from '../core/spacing';

type TypographyCustomProps = {
  textAlign?: React.CSSProperties['textAlign'];
  textTransform?: React.CSSProperties['textTransform'];
  fontWeight?: React.CSSProperties['fontWeight'];
  variant?: TypographyVariant;
  color?: ColorVariant;
  children: React.ReactNode;
  className?: string;
  /** DOM id — lets a dialog point `aria-labelledby` at its visible title. */
  id?: string;
  /**
   * The element to render — `span` where a block is not allowed, e.g. inside a `<button>`; `h1`
   * for the title of a screen, the heading a screen reader lands on (app#242).
   */
  as?: 'div' | 'span' | 'p' | 'h1';
};

type TypographyProps = TypographyCustomProps & FlexingProps & SpacingProps;

const DEFAULT_TEXT_ALIGN = 'left';
const DEFAULT_TEXT_TRANSFORM = 'none';
const DEFAULT_VARIANT = 'large';
const DEFAULT_COLOR = 'grey';

const useStyles = (props: Omit<TypographyCustomProps, 'children'>) => {
  const theme = useTheme();
  const {
    fontWeight,
    textAlign = DEFAULT_TEXT_ALIGN,
    textTransform = DEFAULT_TEXT_TRANSFORM,
    variant = DEFAULT_VARIANT,
    color = DEFAULT_COLOR,
  } = props;

  const coreStyles = theme.typography[variant] || theme.typography[DEFAULT_VARIANT];

  return {
    ...coreStyles,
    // Each step carries its own face and weight (headings and numbers are Big
    // Shoulders 700, text is Public Sans); an explicit prop still wins.
    fontWeight: fontWeight ?? coreStyles.fontWeight,
    textAlign,
    textTransform,
    color: color as ColorVariant,
  };
};

export const Typography = memo((props: TypographyProps) => {
  const { textAlign, textTransform, variant, color, ...rest } = props;
  const styles = useStyles({ textAlign, textTransform, variant, color });

  return <BaseText {...styles} {...rest} />;
});

type BaseTextCustomProps = {
  fontSize: number;
  lineHeight: number;
  fontFamily: string;
  fontWeight: string | number;
  textAlign: string;
  textTransform: string;
  color: ColorVariant;
};

type BaseTextProps = BaseTextCustomProps & FlexingProps & SpacingProps;

const BaseText = styled.div<BaseTextProps>`
  font-size: ${({ fontSize }) => fontSize}px;
  line-height: ${({ lineHeight }) => lineHeight}px;
  font-weight: ${({ fontWeight }) => fontWeight};
  text-align: ${({ textAlign }) => textAlign};
  text-transform: ${({ textTransform }) => textTransform};
  color: ${({ theme, color }) => theme.colors[color]};
  font-family: ${({ fontFamily }) => fontFamily};
  text-decoration: none;
  outline: none;
  /* A heading carries the browser's margin (normalize.css gives h1 0.67em): the text sets its own
     size, so it sets its own spacing too. Before the spacing props, so m/mt/mb/... still win. */
  margin: 0;
  ${formatFlexing}
  ${formatSpacing}
`;
